import { useMemo, useSyncExternalStore } from 'react';
import {
  readPlayerVitals,
  subscribePlayerVitals,
  withLiveVitals,
  type PlayerVitals,
  type VitalsSource,
} from '../../systems/player/livePlayerVitals';

/**
 * One value derived from the local player's live vitals
 * (src/game/systems/player/livePlayerVitals.ts). The component re-renders
 * only when the selected value changes, so select what you show at the
 * precision you show it: `v => Math.round(v.mana)` re-renders once per whole
 * point of regen, `v => v.health / v.maxHealth < 0.3` once per threshold
 * crossing.
 *
 * `select` must return a primitive (compared with Object.is): a new object or
 * array each call would re-render on every store change.
 */
export function usePlayerVital<T extends string | number | boolean | null | undefined>(
  character: VitalsSource | null | undefined,
  select: (vitals: PlayerVitals) => T,
): T {
  const getSnapshot = (): T => select(readPlayerVitals(character));
  return useSyncExternalStore(subscribePlayerVitals, getSnapshot, getSnapshot);
}

/** The labels a whole-point vitals readout shows (current rounded, maxima as sent). */
function displayKey(v: PlayerVitals): string {
  return `${Math.round(v.health)}/${v.maxHealth}/${Math.round(v.mana)}/${v.maxMana}`;
}

/**
 * `character` carrying its live vitals, for readouts that show HP/MP among
 * other fields (character sheet, player list, dev panels). Re-renders when a
 * whole-point value or a maximum changes, not on every fraction of regen.
 */
export function useWithLiveVitals<T extends VitalsSource>(character: T): T;
export function useWithLiveVitals<T extends VitalsSource>(character: T | null): T | null;
export function useWithLiveVitals<T extends VitalsSource>(character: T | null): T | null {
  const key = usePlayerVital(character, displayKey);
  // `key` stands in for the store: a new key means new live values to copy.
  return useMemo(() => (character ? withLiveVitals(character) : character), [character, key]);
}
